import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { Card, CardHeader, CardTitle, CardBody } from '../ui/Card';
import { useAuth } from '../../contexts/AuthContext';
import StaffMetricCards from './analytics/StaffMetricCards';
import StaffAnalyticsCharts from './analytics/StaffAnalyticsCharts';
import DeploymentCharts from './analytics/DeploymentCharts';
import BankCharts from './analytics/BankCharts';
import MetricCards from './analytics/MetricCards';
import CustomerSatisfaction from './analytics/CustomerSatisfaction';
import GuardsAnalytics from './analytics/GuardsAnalytics';
import ClientsAnalytics from './analytics/ClientsAnalytics';

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const AccessSection = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const StaffDashboard: React.FC = () => {
  const { staffData, companyData, user } = useAuth();

  // Check if user is a company owner/admin (not just staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail || 
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // If company owner, show all analytics (same as DashboardBody)
  if (isCompanyOwner) {
    return (
      <BodyContainer>
        <MetricCards />
        <DeploymentCharts />
        <BankCharts />
        <CustomerSatisfaction />
      </BodyContainer>
    );
  }

  // If no staffData, this shouldn't render (should be handled by parent)
  if (!staffData) {
    return null;
  }

  // Check what access/permissions the staff has
  // Dashboard is always accessible for staff, but permissions determine what they can see
  const hasStaffAccess = staffData.access?.staff === true;
  const hasGuardsAccess = staffData.access?.guards === true;
  const hasClientsAccess = staffData.access?.clients === true;
  const hasDeploymentAccess = staffData.access?.deployment === true;
  const dashboardPermission = staffData.permissions?.dashboard || 'view';

  return (
    <BodyContainer>
      {/* Welcome section for staff */}
      <AccessSection>
        <SectionTitle>Welcome, {staffData.displayName || 'Staff Member'}</SectionTitle>
        <Card>
          <CardBody>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: theme.spacing[4],
              padding: theme.spacing[2]
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[1]
                }}>
                  {companyData?.companyName || 'Company'} - {staffData.department || staffData.role}
                </div>
                <div style={{ 
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.light.text.secondary
                }}>
                  Dashboard Access: {dashboardPermission === 'edit' ? 'View & Edit' : 'View Only'}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </AccessSection>

      {/* Show staff metrics and analytics if staff access is granted */}
      {hasStaffAccess && (
        <>
          <AccessSection>
            <SectionTitle>Staff Overview</SectionTitle>
            <StaffMetricCards />
          </AccessSection>
          <AccessSection>
            <SectionTitle>Staff Analytics</SectionTitle>
            <StaffAnalyticsCharts />
          </AccessSection>
        </>
      )}

      {/* Show guards analytics if guards access is granted */}
      {hasGuardsAccess && (
        <AccessSection>
          <SectionTitle>Guards Analytics</SectionTitle>
          <GuardsAnalytics />
        </AccessSection>
      )}

      {/* Show clients analytics if clients access is granted */}
      {hasClientsAccess && (
        <AccessSection>
          <SectionTitle>Clients Analytics</SectionTitle>
          <ClientsAnalytics />
        </AccessSection>
      )}

      {/* Show deployment charts if deployment access is granted */}
      {hasDeploymentAccess && (
        <AccessSection>
          <SectionTitle>Deployment Analytics</SectionTitle>
          <DeploymentCharts />
        </AccessSection>
      )}

      {/* Show bank charts if clients access is granted (banks are part of clients) */}
      {hasClientsAccess && (
        <AccessSection>
          <SectionTitle>Bank Analytics</SectionTitle>
          <BankCharts />
        </AccessSection>
      )}
    </BodyContainer>
  );
};

export default StaffDashboard;

